import { NavLink, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  House, Tray, CalendarBlank, Lightning, ClockCounterClockwise, Sparkle, SignOut, ArrowsClockwise,
} from "@phosphor-icons/react";
import { avatarColor, initials } from "@/lib/format";
import { api, clearToken } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Briefing", icon: House, testid: "nav-briefing", end: true },
  { to: "/inbox", label: "Inbox", icon: Tray, testid: "nav-inbox" },
  { to: "/calendar", label: "Schedule", icon: CalendarBlank, testid: "nav-calendar" },
  { to: "/actions", label: "Pending Actions", icon: Lightning, testid: "nav-actions" },
  { to: "/activity", label: "Activity Log", icon: ClockCounterClockwise, testid: "nav-activity" },
];

export const Sidebar = ({ user, pending = 0, onLogout }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const sync = useMutation({
    mutationFn: api.sync,
    onSuccess: (d) => {
      ["inbox", "calendar", "dashboard", "blocks", "actions", "briefing", "activity"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] })
      );
      toast.success(`Synced ${d.emails} emails · ${d.events} events from Google`);
    },
    onError: (e) => {
      if (e?.response?.status === 428) {
        toast.error("Please sign in again to grant Gmail & Calendar access");
        clearToken();
        navigate("/login");
      } else {
        toast.error(e?.response?.data?.detail || "Sync failed. Please try again.");
      }
    },
  });
  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 border-r border-border bg-white flex flex-col">
      <div className="px-6 py-6 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center">
          <Sparkle weight="fill" className="text-white" size={20} />
        </div>
        <div>
          <div className="font-heading font-extrabold text-lg leading-none tracking-tight">Auppo</div>
          <div className="text-[11px] text-muted-foreground tracking-wide">Executive Assistant</div>
        </div>
      </div>

      <div className="px-3 pb-2">
        <button
          data-testid="sync-btn"
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors disabled:opacity-60"
        >
          <ArrowsClockwise size={16} weight="bold" className={sync.isPending ? "animate-spin" : ""} />
          {sync.isPending ? "Syncing…" : "Sync Gmail & Calendar"}
        </button>
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1">
        {NAV.map(({ to, label, icon: Icon, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon size={19} weight={isActive ? "fill" : "regular"} />
                <span className="flex-1">{label}</span>
                {label === "Pending Actions" && pending > 0 && (
                  <span
                    data-testid="nav-pending-count"
                    className={`text-[11px] font-bold h-5 min-w-5 px-1.5 rounded-full flex items-center justify-center ${
                      isActive ? "bg-white text-slate-900" : "bg-indigo-500 text-white"
                    }`}
                  >
                    {pending}
                  </span>
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-2">
          {user?.avatar ? (
            <img src={user.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold ${avatarColor(user?.name)}`}>
              {initials(user?.name)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={onLogout}
            title="Sign out"
            className="text-slate-400 hover:text-rose-600 transition-colors"
          >
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
};
